import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NzIconModule } from 'ng-zorro-antd/icon';

@Component({
  selector: 'app-mb-button',
  imports: [NgIf, NzIconModule],
  templateUrl: './mb-button.component.html',
})
export class MbButtonComponent {
  @Input() label = '';
  @Input() type:
    | 'primary'
    | 'secondary'
    | 'accent'
    | 'destructive'
    | 'destructiveOutline'
    | 'ghost'
    | 'white-ghost'
    | 'text' = 'primary';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() icon = '';
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() iconSize: 'small' | 'medium' | 'large' = 'medium';
  @Input() htmlType?: 'button' | 'submit' | 'reset' = 'submit';

  @Output() clickHandler = new EventEmitter<void>();

  getButtonClasses(): string {
    const baseClasses =
      'w-full text-mbaza-text-light transition-all duration-300 mb-btn-round flex items-center justify-center gap-2 hover:cursor-pointer truncate';

    let backgroundClasses = '';
    switch (this.type) {
      case 'primary':
        backgroundClasses = 'bg-mbaza-primary';
        break;
      case 'secondary':
        backgroundClasses =
          'bg-gradient-to-r from-mbaza-primary to-mbaza-secondary hover:from-mbaza-primary hover:to-mbaza-primary';
        break;
      case 'destructive':
        backgroundClasses = 'bg-mbaza-red';
        break;
      case 'destructiveOutline':
        backgroundClasses =
          'bg-transparent border border-mbaza-red focus:border-mbaza-red hover:border-mbaza-red';
        break;
      case 'ghost':
        backgroundClasses = 'bg-transparent border border-mbaza-gray-dark ';
        break;
      case 'accent':
        backgroundClasses = 'bg-mbaza-accent';
        break;
      case 'white-ghost':
        backgroundClasses = 'bg-transparent border border-none';
        break;
      case 'text':
        backgroundClasses = 'bg-transparent';
        break;
      default:
        backgroundClasses = 'bg-mbaza-primary';
        break;
    }

    // Add disabled state (but not for loading)
    if (this.disabled && !this.loading) {
      return `${baseClasses} ${backgroundClasses} opacity-50 cursor-not-allowed`;
    }

    // Add loading state (maintains background, just adds cursor)
    if (this.loading) {
      return `${baseClasses} ${backgroundClasses} cursor-not-allowed`;
    }

    return `${baseClasses} ${backgroundClasses}`;
  }

  getIconClasses(): string {
    const baseIconClasses = 'inline-block';

    switch (this.iconSize) {
      case 'small':
        return `${baseIconClasses} text-sm`;
      case 'large':
        return `${baseIconClasses} text-lg`;
      default: // medium
        return `${baseIconClasses} text-base`;
    }
  }

  getSizeClasses(): string {
    const baseIconClasses = '';
    switch (this.size) {
      case 'small':
        return `${baseIconClasses}  h-[46px] lg:h-[50px] px-3 2xl:px-6 2xl:text-xs`;
      case 'large':
        return `${baseIconClasses} h-[52px] lg:h-[56px] 2xl:px-6 2xl:text-xs`;
      default: // default
        return `${baseIconClasses} h-[52px] lg:h-[56px] px-4 2xl:px-8 2xl:text-xs`;
    }
  }

  getLabelClasses(): string {
    const baseLabelClasses = 'font-medium 2xl:font-bold';

    switch (this.type) {
      case 'primary':
        return `${baseLabelClasses}  text-mbaza-text-light`;
      case 'secondary':
        return `${baseLabelClasses} text-mbaza-text-light`;
      case 'ghost':
        return `${baseLabelClasses} text-mbaza-primary`;
      case 'accent':
        return `${baseLabelClasses} text-mbaza-text-light`;
      case 'destructive':
        return `${baseLabelClasses} text-mbaza-text-red`;
      case 'destructiveOutline':
        return `${baseLabelClasses} text-mbaza-text-red`;
      case 'white-ghost':
        return `${baseLabelClasses} text-mbaza-text-light`;
      case 'text':
        return `${baseLabelClasses}`;
      default:
        return `${baseLabelClasses} text-mbaza-text-light`;
    }
  }

  shouldShowIcon(): boolean {
    return Boolean(this.icon && this.icon.trim() !== '');
  }

  shouldShowSpinner(): boolean {
    return this.loading;
  }

  handleClick(): void {
    if (!this.disabled && !this.loading) {
      this.clickHandler.emit();
    }
  }
}
